'use client';

import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  Check, 
  ArrowRight, 
  Users, 
  Shield, 
  Clock, 
  BarChart3,
  MessageSquare,
  Mail,
  Phone,
  Video,
  Sparkles,
  FileText,
} from 'lucide-react';
import { LandingNavbar } from '../home';
import HomeFooter from '../home/HomeFooter';
import { useContactForm, ContactFormSubmission } from '../../../hooks/useContactForm';
import { validateContactForm } from '../../../lib/contact-form-validation';

export function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    instituteName: '',
    contactNumber: '',
    email: '',
    studentCount: '',
    campuses: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const { mutate: submitContactForm, isLoading: isSubmitting } = useContactForm();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'contactNumber') {
      const cleanedDigits = value.replace(/\D/g, '').slice(0, 10);

      if (cleanedDigits === formData.contactNumber) {
        return;
      }

      setFormData(prev => ({ ...prev, [name]: cleanedDigits }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const validation = validateContactForm(formData);
    if (validation.isValid) {
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }

    if (validation.errors[name]) {
      setErrors(prev => ({ ...prev, [name]: validation.errors[name] }));
    } else if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateContactForm(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setErrors({});
    
    const submissionData: ContactFormSubmission = {
      name: validation.data!.name,
      role: validation.data!.role,
      instituteName: validation.data!.instituteName,
      numberOfStudents: validation.data!.studentCount ? String(validation.data!.studentCount) : undefined,
      numberOfCampuses: validation.data!.campuses ? String(validation.data!.campuses) : undefined,
      message: validation.data!.message || undefined,
      contactNumber: validation.data!.contactNumber,
      email: validation.data!.email || undefined,
    };
    
    submitContactForm(submissionData, {
      onSuccess: () => {
        setFormData({
          name: '',
          role: '',
          instituteName: '',
          contactNumber: '',
          email: '',
          studentCount: '',
          campuses: '',
          message: '',
        });
        setErrors({});
      },
    });
  };

  const includedFeatures = [
    { icon: Users, text: 'Core modules for students, staff, fees, attendance, notices, and transport' },
    { icon: MessageSquare, text: 'Parent app with QR payments, proof uploads, receipts, and leave requests' },
    { icon: Shield, text: 'Role-based access controls, branding, and data exports' },
    { icon: Clock, text: 'Implementation support: configuration, training, rollout guidance' },
    { icon: Sparkles, text: 'Ongoing updates, roadmap visibility, and dedicated customer success' },
  ];

  const optionalAddOns = [
    { icon: BarChart3, title: 'BigQuery-based audit logs', description: 'Advanced compliance needs' },
    { icon: MessageSquare, title: 'Extended WhatsApp workflows', description: 'Custom templates, regional languages' },
    { icon: Sparkles, title: 'AI-assisted automations', description: 'Reconciliation, reminders, and anomaly detection' },
    { icon: FileText, title: 'Custom reports and analytics', description: 'Tailored analytics packages' },
  ];

  const pricingFeatures = [
    'Tiered by institute size, sessions, and required modules',
    'Volume-based pricing for groups or multi-campus networks',
    'Annual and multi-year agreements with onboarding bundles',
    'Transparent renewal process with roadmap and KPI review',
  ];

  const implementationSteps = [
    { step: 'Discovery', description: 'Understand your needs and priorities' },
    { step: 'Configuration', description: 'Set up your Nest instance' },
    { step: 'Pilot', description: 'Test with a small group' },
    { step: 'Rollout', description: 'Full deployment across your school' },
    { step: 'Optimize', description: 'Continuous improvement and support' },
  ];

  const cssVars = { '--top-navbar-height': '64px' } as React.CSSProperties;

  return (
    <div className="min-h-screen w-full bg-white text-[#1D2125] font-sans" style={cssVars}>
      <div className="w-full mx-auto pt-[var(--top-navbar-height,64px)]">
        <LandingNavbar />

        {/* Contact Form Section */}
        <div id="contact-form" className="px-3 xs:px-4 sm:px-6 md:px-8 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-20 mx-2 sm:mx-4 lg:mx-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8 xs:mb-10 sm:mb-12">
              <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-[#1D2125] mb-3 xs:mb-4">
                Get in Touch with the Nest Team
              </h2>
              <p className="text-sm xs:text-base sm:text-lg text-[#4D4F52] px-2 xs:px-4 sm:px-0">
              Ready to modernize your school operations? Our team is here to help you get started or answer any questions about the Nest platform 
              </p>
              <p className="text-xs xs:text-sm text-[#7A7D81] mt-3 xs:mt-4 font-medium px-2 xs:px-4 sm:px-0">
                We'll respond within two business day with a tailored walkthrough plan.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-4 xs:p-6 sm:p-8 shadow-lg border border-[#E4E8EA]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 xs:gap-5 sm:gap-6 mb-4 xs:mb-5 sm:mb-6">
                <div>
                  <label htmlFor="name" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    maxLength={150}
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.name 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="Your name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="role" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Role <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    required
                    maxLength={150}
                    value={formData.role}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.role 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="Principal, Admin, etc."
                  />
                  {errors.role && (
                    <p className="mt-1 text-xs text-red-600">{errors.role}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="instituteName" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Institute Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="instituteName"
                    name="instituteName"
                    required
                    maxLength={150}
                    value={formData.instituteName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.instituteName 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="Your school name"
                  />
                  {errors.instituteName && (
                    <p className="mt-1 text-xs text-red-600">{errors.instituteName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="contactNumber" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Contact Number <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    required
                    maxLength={20}
                    value={formData.contactNumber}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.contactNumber 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="e.g., +91 98765 43210"
                  />
                  {errors.contactNumber && (
                    <p className="mt-1 text-xs text-red-600">{errors.contactNumber}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    maxLength={150}
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.email 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="studentCount" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Number of Students (optional)
                  </label>
                  <input
                    type="number"
                    id="studentCount"
                    name="studentCount"
                    min="1"
                    value={formData.studentCount}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.studentCount 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="e.g., 500"
                  />
                  {errors.studentCount && (
                    <p className="mt-1 text-xs text-red-600">{errors.studentCount}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="campuses" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                    Number of Campuses (optional)
                  </label>
                  <input
                    type="number"
                    id="campuses"
                    name="campuses"
                    min="1"
                    value={formData.campuses}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                      errors.campuses 
                        ? 'border-red-300 focus:ring-red-500' 
                        : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                    }`}
                    placeholder="e.g., 1"
                  />
                  {errors.campuses && (
                    <p className="mt-1 text-xs text-red-600">{errors.campuses}</p>
                  )}
                </div>
              </div>

              <div className="mb-4 xs:mb-5 sm:mb-6">
                <label htmlFor="message" className="block text-xs xs:text-sm font-medium text-[#1D2125] mb-1.5 xs:mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={3000}
                  value={formData.message}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent resize-none ${
                    errors.message 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-[#E4E8EA] focus:ring-[#0D373D]'
                  }`}
                  placeholder="Optional message..."
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.message ? (
                    <p className="text-xs text-red-600">{errors.message}</p>
                  ) : (
                    <div></div>
                  )}
                  <p className="text-xs text-gray-500">
                    {formData.message.length}/3000 characters
                  </p>
                </div>
              </div>


              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto min-w-[180px] xs:min-w-[200px] flex items-center justify-center px-4 xs:px-5 sm:px-6 py-2.5 xs:py-3 bg-[#0D373D] text-white text-sm xs:text-base font-semibold rounded-xl hover:bg-[#0A2A2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>

     

        <div className="bg-[#F5F8F9] mx-2 sm:mx-4 lg:mx-8 rounded-xl">
          <HomeFooter />
        </div>
      </div>
    </div>
  );
}

function PreferredChannelSelect(props: {
  id: string;
  name: string;
  value: string;
  onChange: (val: string) => void;
}) {
  const { id, value, onChange } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const options: { value: string; label: string; Icon: any }[] = [
    { value: 'email', label: 'Email', Icon: Mail },
    { value: 'phone', label: 'Phone', Icon: Phone },
    { value: 'whatsapp', label: 'WhatsApp', Icon: MessageSquare },
    { value: 'video-call', label: 'Video Call', Icon: Video },
  ];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className="w-full px-3 xs:px-4 py-2 xs:py-2.5 text-sm xs:text-base border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent border-[#E4E8EA] focus:ring-[#0D373D] flex items-center justify-between"
      >
        <div className="flex items-center space-x-3">
          {selected ? (
            <>
              <selected.Icon className="h-4 w-4 text-[#0D373D]" />
              <span className="text-sm text-[#1D2125]">{selected.label}</span>
            </>
          ) : (
            <span className="text-sm text-[#7A7D81]">Select an option</span>
          )}
        </div>
        <svg className={`h-4 w-4 text-[#7A7D81] transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-labelledby={id}
          className="absolute z-30 mt-2 w-full bg-white border border-[#E4E8EA] rounded-lg shadow-lg py-1"
        >
          {options.map(opt => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`cursor-pointer px-3 py-2 flex items-center space-x-3 text-sm ${
                opt.value === value ? 'bg-[#F0FAF9] text-[#0D373D]' : 'text-[#1D2125] hover:bg-[#F5F8F9]'
              }`}
            >
              <opt.Icon className="h-4 w-4" />
              <span>{opt.label}</span>
            </li>
          ))}
        </ul>
      )}
      <input type="hidden" name={props.name} value={value} />
    </div>
  );
}


